import React from 'react';
import {
  Container,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
} from '@mui/material';
import AuthorsSidebar from '../../components/AuthorsSidebar';
import {
  Public,
  Speed,
  Visibility,
  Copyright,
  CheckCircle,
  Info,
} from '@mui/icons-material';

const OpenAccess: React.FC = () => {
  const openAccessBenefits = [
    {
      title: 'Свободный доступ',
      description: 'Статьи доступны всем пользователям без ограничений',
      icon: <Public color="primary" />,
    },
    {
      title: 'Быстрое распространение',
      description: 'Результаты исследований становятся доступными немедленно',
      icon: <Speed color="success" />,
    },
    {
      title: 'Повышенная видимость',
      description: 'Статьи получают больше цитирований и внимания',
      icon: <Visibility color="info" />,
    },
    {
      title: 'Соблюдение авторских прав',
      description: 'Авторы сохраняют права на свои работы',
      icon: <Copyright color="warning" />,
    },
  ];

  const licenseTypes = [
    {
      name: 'CC BY',
      description: 'Creative Commons Attribution',
      details: 'Позволяет другим распространять, изменять и использовать работу, даже в коммерческих целях, при условии указания авторства',
    },
    {
      name: 'CC BY-SA',
      description: 'Creative Commons Attribution-ShareAlike',
      details: 'Позволяет другим распространять, изменять и использовать работу, даже в коммерческих целях, при условии указания авторства и лицензирования производных работ на тех же условиях',
    },
    {
      name: 'CC BY-NC',
      description: 'Creative Commons Attribution-NonCommercial',
      details: 'Позволяет другим распространять, изменять и использовать работу в некоммерческих целях при условии указания авторства',
    },
  ];

  const publicationSteps = [
    'Подготовка статьи в соответствии с требованиями',
    'Подача статьи через систему журнала',
    'Прохождение рецензирования',
    'Принятие статьи к публикации',
    'Публикация в открытом доступе',
  ];

  return (
    <AuthorsSidebar>
      <Container sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        Open Access
      </Typography>
      
      <Typography variant="body1" paragraph>
        Наш журнал поддерживает принципы открытого доступа к научным публикациям, 
        что способствует более широкому распространению знаний и повышению качества исследований.
      </Typography>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Info />
          Что такое Open Access
        </Typography>
        <Typography variant="body1" paragraph>
          Open Access (открытый доступ) — это модель публикации научных работ, при которой 
          статьи доступны всем пользователям интернета бесплатно и без ограничений.
        </Typography>
        <Typography variant="body1" paragraph>
          Это означает, что читатели могут свободно читать, загружать, копировать, 
          распространять, печатать, искать или ссылаться на полные тексты статей.
        </Typography>
      </Paper>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Преимущества Open Access
        </Typography>
        <Grid container spacing={3}>
          {openAccessBenefits.map((benefit, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    {benefit.icon}
                    <Typography variant="h6" sx={{ ml: 1 }}>
                      {benefit.title}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {benefit.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Лицензии Open Access
        </Typography>
        <Typography variant="body1" paragraph>
          Наш журнал использует лицензии Creative Commons для обеспечения открытого доступа 
          к публикациям при соблюдении авторских прав.
        </Typography>
        
        {licenseTypes.map((license, index) => (
          <Card key={index} sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Chip label={license.name} color="primary" />
                <Typography variant="h6" sx={{ ml: 2 }}>
                  {license.description}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {license.details}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Как опубликовать статью в Open Access
        </Typography>
        <Typography variant="body1" paragraph>
          Процесс публикации статьи в открытом доступе не отличается от обычной публикации, 
          но имеет свои особенности.
        </Typography>
        
        <Typography variant="h6" gutterBottom>
          Этапы публикации:
        </Typography>
        <List>
          {publicationSteps.map((step, index) => (
            <ListItem key={index}>
              <ListItemIcon>
                <CheckCircle color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary={`${index + 1}. ${step}`}
                sx={{
                  '& .MuiListItemText-primary': {
                    fontWeight: 'medium'
                  }
                }}
              />
            </ListItem>
          ))}
        </List>
        
        <Box sx={{ mt: 3, p: 2, backgroundColor: 'primary.light', borderRadius: 1 }}>
          <Typography variant="body2" color="primary.contrastText">
            <strong>Важно:</strong> Все статьи, опубликованные в нашем журнале, 
            автоматически становятся доступными в открытом доступе под лицензией CC BY.
          </Typography>
        </Box>
      </Paper>
      </Container>
    </AuthorsSidebar>
  );
};

export default OpenAccess;
